/**
 * 睡眠视图数据工具函数
 * 包含纯函数方法，不依赖组件实例
 */

import * as sleepViewUtils from './sleepViewUtils';

/**
 * 解析时间字符串
 * @param {string} timeStr - 时间字符串
 * @returns {Date|null} 解析后的日期对象
 */
export function parseTimeString(timeStr) {
  return sleepViewUtils.parseTimeString(timeStr);
}

/**
 * 基于指定日期解析时间字符串
 * @param {string} timeStr - 时间字符串
 * @param {Date} appointmentDate - 预约日期
 * @returns {Date|null} 解析后的日期对象
 */
export function parseTimeStringWithDate(timeStr, appointmentDate) {
  return sleepViewUtils.parseTimeStringWithDate(timeStr, appointmentDate);
}

/**
 * 获取剩余时间（分钟数）
 * @param {Object} appointment - 预约对象
 * @returns {number} 剩余分钟数，如果未开始则返回很大的数字
 */
export function getRemainingTimeInMinutes(appointment) {
  if (!appointment || !appointment.startTime || !appointment.endTime) {
    return Number.MAX_SAFE_INTEGER;
  }

  const now = new Date();
  const startTime = parseTimeString(appointment.startTime);
  const endTime = parseTimeString(appointment.endTime);

  if (!startTime || !endTime) {
    return Number.MAX_SAFE_INTEGER;
  }

  if (now >= endTime) {
    return 0;
  }

  if (now < startTime) {
    const totalMinutes = Math.floor(
      (endTime.getTime() - startTime.getTime()) / (1000 * 60)
    );
    return totalMinutes + 10000;
  }

  const remainingMs = endTime.getTime() - now.getTime();
  return Math.ceil(remainingMs / (1000 * 60));
}

/**
 * 获取实际剩余时间（格式化字符串）
 * @param {Object} appointment - 预约对象
 * @returns {string} 格式化的剩余时间字符串
 */
export function getActualRemainingTime(appointment) {
  if (!appointment || !appointment.startTime || !appointment.endTime) {
    return '无数据';
  }

  const now = new Date();
  const startTime = parseTimeString(appointment.startTime);
  const endTime = parseTimeString(appointment.endTime);

  if (!startTime || !endTime) {
    return '时间格式错误';
  }

  if (now >= endTime) {
    return '已完成';
  }

  if (now < startTime) {
    const totalMinutes = Math.floor(
      (endTime.getTime() - startTime.getTime()) / (1000 * 60)
    );
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0
      ? `${hours}小时${minutes}分钟`
      : `${minutes}分钟`;
  }

  const remainingMs = endTime.getTime() - now.getTime();
  const remainingMinutes = Math.ceil(remainingMs / (1000 * 60));

  if (remainingMinutes <= 0) {
    return '即将完成';
  }

  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;
  
  return hours > 0
    ? `${hours}小时${minutes}分钟`
    : `${minutes}分钟`;
}

/**
 * 获取睡眠气泡值（随机值）
 * @param {string} sleepStatus - 睡眠状态
 * @returns {number} 气泡值（1-3）
 */
export function getSleepBubbleValue(sleepStatus) {
  const random = Math.random() * 100; // 0-100的随机数
  let bubbleValue = 1; // 默认值
  
  switch (sleepStatus) {
    case '深睡':
      // 60%概率+3分，30%概率+2分，10%概率+1分
      if (random < 60) {
        bubbleValue = 3;
      } else if (random < 90) {
        bubbleValue = 2;
      } else {
        bubbleValue = 1;
      }
      break;
    case '浅睡':
      // 30%概率+1分，60%概率+2分，10%概率+3分
      if (random < 30) {
        bubbleValue = 1;
      } else if (random < 90) {
        bubbleValue = 2;
      } else {
        bubbleValue = 3;
      }
      break;
    case '清醒':
      // 90%概率+1分，10%概率+2分
      if (random < 90) {
        bubbleValue = 1;
      } else {
        bubbleValue = 2;
      }
      break;
    default:
      // 确保即使是未知状态也有气泡值
      bubbleValue = 1;
  }
  
  return bubbleValue;
}

/**
 * 生成基于患者信息的随机种子
 * @param {Object} item - 患者信息对象
 * @returns {number} 种子值
 */
export function generatePatientSeed(item) {
  // 使用患者姓名、房间号、开始时间等信息生成固定种子
  const seedString = `${item.patientName || ''}_${item.roomName || ''}_${item.startTime || ''}`;
  let hash = 0;
  for (let i = 0; i < seedString.length; i++) {
    const char = seedString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 转换为32位整数
  }
  return Math.abs(hash);
}

/**
 * 从字符串生成数字种子
 * @param {string} name - 字符串
 * @returns {number} 种子值
 */
export function generateSeedFromName(name) {
  let seed = 0;
  for (let i = 0; i < name.length; i++) {
    seed += name.charCodeAt(i);
  }
  return seed;
}

/**
 * 基于种子生成指定范围内的值
 * @param {number} seed - 种子值
 * @param {number} min - 最小值
 * @param {number} max - 最大值
 * @returns {number} 生成的值
 */
export function generateSeededValue(seed, min, max) {
  // 简单的伪随机数生成器
  const x = Math.sin(seed) * 10000;
  const random = x - Math.floor(x);
  return min + random * (max - min);
}

/**
 * 生成虚拟脑波数据
 * @param {string} patientName - 患者姓名
 * @param {string} currentSleepState - 当前睡眠状态，默认为'浅睡'
 * @returns {Object} 脑波数据对象
 */
export function generateVirtualBrainwaveData(patientName, currentSleepState = '浅睡') {
  // 基于患者姓名生成种子，确保同一患者的数据一致
  const seed = generateSeedFromName(patientName || 'default');
  
  // 根据当前睡眠状态生成不同的脑波模式，增强对比度
  let pattern;
  
  switch(currentSleepState) {
    case '清醒':
      pattern = { delta: 3, theta: 8, alpha: 40, beta: 45, gamma: 4 };
      break;
    case '浅睡':
      pattern = { delta: 35, theta: 40, alpha: 15, beta: 8, gamma: 2 };
      break;
    case '深睡':
      pattern = { delta: 70, theta: 20, alpha: 6, beta: 3, gamma: 1 };
      break;
    default:
      pattern = { delta: 35, theta: 40, alpha: 15, beta: 8, gamma: 2 };
  }
  
  // 大幅增强时间变化因子，让数据有更显著的动态变化
  const timeNow = Date.now();
  const slowWave = Math.sin(timeNow / 3000) * 0.6; // 3秒周期的慢波动，大幅增强幅度
  const fastWave = Math.sin(timeNow / 600) * 0.4; // 0.6秒周期的快波动，大幅增强幅度
  const mediumWave = Math.sin(timeNow / 1500) * 0.35; // 1.5秒周期的中等波动，增强幅度
  const ultraFastWave = Math.sin(timeNow / 300) * 0.25; // 0.3秒周期的超快波动，增加细节变化
  const randomFactor = (Math.random() - 0.5) * 0.5; // 大幅增强随机因子
  
  // 为每个波段生成独特的时间变化模式，确保每个数值都有动态变化
  const deltaTimeVariation = Math.sin(timeNow / 2800 + seed) * 0.4 + Math.cos(timeNow / 1200 + seed * 2) * 0.3;
  const thetaTimeVariation = Math.sin(timeNow / 2200 + seed * 3) * 0.45 + Math.cos(timeNow / 800 + seed * 4) * 0.25;
  const alphaTimeVariation = Math.sin(timeNow / 1800 + seed * 5) * 0.5 + Math.cos(timeNow / 1000 + seed * 6) * 0.35;
  const betaTimeVariation = Math.sin(timeNow / 1400 + seed * 7) * 0.4 + Math.cos(timeNow / 700 + seed * 8) * 0.3;
  const gammaTimeVariation = Math.sin(timeNow / 1600 + seed * 9) * 0.35 + Math.cos(timeNow / 900 + seed * 10) * 0.25;
  
  // 生成基础脑波数据（百分比），结合固定偏移和动态变化
  const baseValues = {
    delta: pattern.delta + generateSeededValue(seed + 1, -15, 15) + deltaTimeVariation * 20, // 固定偏移±15 + 动态变化±8
    theta: pattern.theta + generateSeededValue(seed + 2, -15, 15) + thetaTimeVariation * 20, // 固定偏移±15 + 动态变化±9
    alpha: pattern.alpha + generateSeededValue(seed + 3, -12, 12) + alphaTimeVariation * 15, // 固定偏移±12 + 动态变化±7.5
    beta: pattern.beta + generateSeededValue(seed + 4, -10, 10) + betaTimeVariation * 12,   // 固定偏移±10 + 动态变化±4.8
    gamma: pattern.gamma + generateSeededValue(seed + 5, -5, 5) + gammaTimeVariation * 8    // 固定偏移±5 + 动态变化±2.8
  };
  
  // 应用多层次的动态变化因子，确保每个波段都有独特且持续的变化
  const fluctuatedValues = {
    delta: Math.max(0.5, baseValues.delta + 
      (slowWave * 0.6 + randomFactor * 0.8 + mediumWave * 0.4 + ultraFastWave * 0.3 + deltaTimeVariation * 0.5) * Math.abs(baseValues.delta) * 0.4),
    theta: Math.max(0.5, baseValues.theta + 
      (fastWave * 0.7 - randomFactor * 0.6 + slowWave * 0.3 + ultraFastWave * 0.4 + thetaTimeVariation * 0.6) * Math.abs(baseValues.theta) * 0.45),
    alpha: Math.max(0.5, baseValues.alpha + 
      (slowWave * 0.5 + fastWave * 0.4 + mediumWave * 0.8 + ultraFastWave * 0.2 + alphaTimeVariation * 0.7) * Math.abs(baseValues.alpha) * 0.5),
    beta: Math.max(0.5, baseValues.beta + 
      (fastWave * 0.8 + randomFactor * 0.7 + mediumWave * 0.3 + ultraFastWave * 0.5 + betaTimeVariation * 0.6) * Math.abs(baseValues.beta) * 0.55),
    gamma: Math.max(0.5, baseValues.gamma + 
      (slowWave * 0.4 - fastWave * 0.5 + randomFactor * 0.9 + ultraFastWave * 0.6 + gammaTimeVariation * 0.8) * Math.abs(baseValues.gamma) * 0.6)
  };
  
  // 标准化为百分比，确保总和为100%，同时保证每个波段都有最小值
  const total = Object.values(fluctuatedValues).reduce((sum, val) => sum + val, 0);
  const minValue = 1; // 确保每个波段至少有1%
  const normalizedValues = {};
  
  // 先进行初步标准化
  Object.keys(fluctuatedValues).forEach(key => {
    const normalized = (fluctuatedValues[key] / total) * 100;
    normalizedValues[key] = Math.max(minValue, Math.round(normalized));
  });
  
  // 处理舍入误差和最小值约束，确保总和精确为100
  let currentTotal = Object.values(normalizedValues).reduce((sum, val) => sum + val, 0);
  const diff = 100 - currentTotal;
  
  if (diff !== 0) {
    // 如果总和超过100，从最大的波段中减去差值（但不能低于最小值）
    if (diff < 0) {
      const sortedKeys = Object.keys(normalizedValues).sort((a, b) => normalizedValues[b] - normalizedValues[a]);
      let remaining = Math.abs(diff);
      
      for (const key of sortedKeys) {
        if (remaining <= 0) break;
        const canReduce = normalizedValues[key] - minValue;
        const toReduce = Math.min(remaining, canReduce);
        normalizedValues[key] -= toReduce;
        remaining -= toReduce;
      }
    } else {
      // 如果总和小于100，将差值分配给最大的波段
      const maxKey = Object.keys(normalizedValues).reduce((a, b) => 
        normalizedValues[a] > normalizedValues[b] ? a : b
      );
      normalizedValues[maxKey] += diff;
    }
  }
  
  // 最终验证：确保没有波段为0，如果有则重新分配
  Object.keys(normalizedValues).forEach(key => {
    if (normalizedValues[key] < minValue) {
      normalizedValues[key] = minValue;
    }
  });
  
  // 重新计算总和并调整
  currentTotal = Object.values(normalizedValues).reduce((sum, val) => sum + val, 0);
  if (currentTotal !== 100) {
    const adjustment = 100 - currentTotal;
    const maxKey = Object.keys(normalizedValues).reduce((a, b) => 
      normalizedValues[a] > normalizedValues[b] ? a : b
    );
    normalizedValues[maxKey] += adjustment;
  }
  
  // 验证总和确实为100%，并确保没有波段为0
  const finalTotal = Object.values(normalizedValues).reduce((sum, val) => sum + val, 0);
  const hasZeroValue = Object.values(normalizedValues).some(val => val <= 0);
  
  if (finalTotal !== 100) {
    console.warn(`脑波数据总和不为100%: ${finalTotal}%`, normalizedValues);
  }
  
  if (hasZeroValue) {
    console.warn(`检测到脑波数据中有0值或负值:`, normalizedValues);
    // 强制修正0值或负值
    Object.keys(normalizedValues).forEach(key => {
      if (normalizedValues[key] <= 0) {
        normalizedValues[key] = 1;
      }
    });
  }
  
  return {
    delta: normalizedValues.delta,
    theta: normalizedValues.theta,
    alpha: normalizedValues.alpha,
    beta: normalizedValues.beta,
    gamma: normalizedValues.gamma,
    // 添加虚拟数据标识
    isVirtualBrainwave: true
  };
}

/**
 * 格式化患者姓名
 * @param {string} patientName - 患者姓名
 * @returns {string} 格式化后的姓名
 */
export function formatPatientName(patientName) {
  return sleepViewUtils.formatPatientName(patientName);
}

/**
 * 获取治疗状态
 * @param {Object} appointment - 预约对象
 * @returns {string} 治疗状态
 */
export function getTreatmentStatus(appointment) {
  return sleepViewUtils.getTreatmentStatus(appointment);
}

